import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Package, Factory, Truck, BarChart3, TrendingUp, ChevronLeft, ChevronRight, LogOut, Settings, Receipt, ClipboardList, ClipboardCheck, Layers, CalendarDays, HardHat, ArrowDownToLine } from 'lucide-react';
import { approvalAPI } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: ClipboardCheck, label: 'Approvals', path: '/dashboard/approvals' },
  { icon: ClipboardList, label: 'Indents', path: '/dashboard/indents' },
  { icon: ShoppingCart, label: 'Purchase', path: '/dashboard/purchase' },
  { icon: ArrowDownToLine, label: 'Inwards', path: '/dashboard/inwards' },
  { icon: Package, label: 'Stock', path: '/dashboard/stock' },
  { icon: Layers, label: 'BOMs', path: '/dashboard/boms' },
  { icon: CalendarDays, label: 'Planning', path: '/dashboard/planning' },
  { icon: Factory, label: 'Production', path: '/dashboard/production' },
  { icon: Receipt, label: 'Sales', path: '/dashboard/sales' },
  { icon: Truck, label: 'Dispatches', path: '/dashboard/dispatches' },
  { icon: HardHat, label: 'Site Projects', path: '/dashboard/site-projects' },
  { icon: BarChart3, label: 'Reports', path: '/dashboard/reports' },
  { icon: TrendingUp, label: 'Forecast', path: '/dashboard/forecast' },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
];

const REPORT_PERM_KEYS = [
  'reports.sales', 'reports.sub_category_sales', 'reports.margins', 'reports.invoice_recon',
  'reports.amended', 'reports.purchase', 'reports.stock', 'reports.production',
  'reports.dispatch', 'reports.vendor', 'reports.debtors', 'reports.payment_ageing',
  'reports.stock_ageing', 'reports.reorder',
];

const SALES_PERM_KEYS = [
  'oc.ready_to_dispatch', 'oc.add_invoice', 'oc.dispatch', 'oc.confirm_dispatch',
  'oc.cancel', 'oc.bulk_status', 'oc.amend', 'oc.approve_amendment',
  'sales_return.create', 'sales_return.verify', 'sales_return.approve',
  'quotation.delete', 'sales_target.manage',
];

const Sidebar = ({ collapsed, onToggle, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isActive = (path) => path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(path);
  const handleLogout = () => { localStorage.removeItem('prismoline_user'); localStorage.removeItem('prismoline_token'); navigate('/login'); };
  const [pendingCount, setPendingCount] = useState(0);

  const hasAnyReportPermission = user.role === 'Admin' || REPORT_PERM_KEYS.some(k => hasPermission(k));
  const hasAnySalesPermission = user.role === 'Admin' || SALES_PERM_KEYS.some(k => hasPermission(k));

  useEffect(() => {
    if (['Admin', 'Manager'].includes(user.role)) {
      approvalAPI.getStats().then(s => setPendingCount(s.pending)).catch(() => {});
      const interval = setInterval(() => {
        approvalAPI.getStats().then(s => setPendingCount(s.pending)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user.role]);

  return (
    <div className={`fixed top-0 left-0 h-screen bg-white border-r border-gray-100 flex flex-col z-40 transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-[250px]'}`}>
      <div className="h-14 flex items-center px-4 border-b border-gray-100">
        <Link to="/dashboard" data-testid="sidebar-home-link" className="flex items-center gap-2 overflow-hidden">
          <img src="/logo.png" alt="Prismoline" className="flex-shrink-0" style={{ height: collapsed ? '26px' : '32px', objectFit: 'contain' }} />
        </Link>
      </div>

      <button data-testid="sidebar-collapse-button" onClick={onToggle} className="absolute -right-3 top-[72px] w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 z-50">
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </button>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {menuItems.map((item) => {
          if (user.role === 'Operator' && item.label === 'Sales' && !hasAnySalesPermission) return null;
          if (user.role === 'Operator' && ['Forecast', 'Approvals'].includes(item.label)) return null;
          if (item.label === 'Reports' && !hasAnyReportPermission) return null;
          if (user.role === 'Salesman' && ['Production', 'Purchase'].includes(item.label)) return null;
          const showBadge = item.label === 'Approvals' && pendingCount > 0 && ['Admin', 'Manager'].includes(user.role);
          return (
            <Link key={item.path} to={item.path} title={collapsed ? item.label : undefined}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 relative ${isActive(item.path) ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
              style={isActive(item.path) ? { backgroundColor: '#24549c' } : {}}>
              <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(item.path) ? 'text-white' : ''}`} />
              {!collapsed && <span>{item.label}</span>}
              {showBadge && (
                <span className={`${collapsed ? 'absolute -top-0.5 -right-0.5' : 'ml-auto'} inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full bg-amber-500`}>
                  {pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-gray-100 py-2 px-2 space-y-0.5">
        {!collapsed && (
          <div className="px-3 py-2 mb-1">
            <p className="text-xs font-semibold text-gray-900 truncate">{user.name}</p>
            <p className="text-[10px] text-gray-400 truncate">{user.role} &bull; {user.email}</p>
          </div>
        )}
        <button onClick={handleLogout} title={collapsed ? 'Logout' : undefined}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium text-red-500 hover:bg-red-50 transition-colors">
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
